<template>
  <div class="col-12 product-sector px-3">
    <div class="row">
      <!-- header -->
      <div class="col-12">
        <div class="product-sector__header py-3">
          <div class="product-sector__text">
            <el-checkbox :indeterminate="state.isIndeterminate" v-model="state.check_all" @change="handleCheckAllChange">
              {{ t("global.selectAll") }}
            </el-checkbox>
            {{ t("carts.currentlySelected") }}
            <span class="product-sector__highlight-text">{{ cartCount }}</span>
            {{ $t("carts.items", $n(cartCount)) }}
          </div>
        </div>
      </div>

      <div class="col-12 product-sector__content open">
        <div v-for="(g, gk) in cartEventGroupsFormatted">
          <!-- group header -->
          <div class="product-sector__group-title">{{ g.cart_group_name }}
            <a v-if="gk != 'null'" :href="'/promotions/'+g.prom_id" class="btn btn-outline-light py-0 px-2 border-0">
              <i class="bi bi-arrow-right-circle mr-1"></i>
              {{ t("carts.goToEvent") }}
            </a>
          </div>

          <!-- table header -->
          <div class="col-12">
            <div class="product-sector__table-header d-none d-md-block">
              <div class="table-head-sector">
                <div class="table-head-title">{{ t("orders.productDetails") }}</div>
                <div class="table-head-title">{{ t("orders.qty") }}</div>
                <div class="table-head-title">{{ t("orders.price") }}</div>
                <div class="table-head-title">&nbsp;</div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <!-- productItem -->
            <div
              v-for="n in g.items"
              :key="n.cart_id"
              class="product-group"
            >
              <div class="product-group__content">
                <div class="product-item row">
                  <div class="col-3 col-sm-2 product-item__col product-item__col--left">
                    <div class="product-item__img-block">
                      <div class="product-item__checkbox">
                        <el-checkbox v-model="n.checked" :disabled="!n.is_valid" @change="(val) => handleChange(val, n.cart_id)"></el-checkbox>
                      </div>
                      <div class="product-item__img">
                        <img class="lazyload" :src="n.image_url">
                      </div>
                    </div>
                  </div>
                  <div class="col-9 col-sm-10 product-item__col product-item__col--right">
                    <div class="row">
                      <!-- 名稱 -->
                      <div class="col-10 col-md-5 product-item__name">
                        <div>
                          <span v-if="n.is_excluded_discount" class="badge badge-primary mr-1">不適用其他折扣</span>
                          <span v-if="n.is_excluded_outer_island" class="badge badge-primary mr-1">不支援外島</span>
                          <span v-if="n.single_shipping_quantity !== -1" class="badge badge-primary mr-1">單次出貨數量: {{n.single_shipping_quantity}}</span>
                        </div>
                        <div class="product-item__name-main">
                          <span>{{ n.product_name }}</span>
                        </div>
                        <div class="product-item__name-sub">
                          <span v-if="n.discounted_price">
                            {{ n.symbol }} {{ n.discounted_price }} - {{ n.assort_name }}
                          </span>
                          <span v-else>
                            {{ n.symbol }} {{ n.sale_price }} - {{ n.assort_name }}
                          </span>
                        </div>
                      </div>

                      <!-- 動作(右上角) -->
                      <div class="col-2 d-block d-md-none">
                        <div class="product-item__tool">
                          <i class="bi bi-x-circle-fill product-item-tool__icon" @click="deleteCart(n.cart_id)"></i>
                        </div>
                      </div>

                      <!-- 數量 -->
                      <div class="col-6 col-md-4 product-item__qty">
                        <div class="product-item__qty-select">
                          <el-form-item
                            :prop="'carts.' + n?.carts_idx + '.quantity'"
                            :show-message="false"
                            :rules="[
                              {
                                required: false,
                                type: 'number',
                                min: n.min_order_quantity,
                                max: n.max_order_quantity,
                              },
                            ]"
                          >
                            <el-input-number
                              v-model="n.quantity"
                              :min="n.min_order_quantity"
                              @change="(value) => changeQty(n.cart_id, value)"
                            />
                          </el-form-item>
                        </div>
                        <div class="product-item__qty-hint text-left" style="margin-top: -16px">
                          <span v-if="n.status == 'delete'">{{ t("carts.modelInvalid") }}</span>
                          <span v-else-if="n.status == 'event_delete'">異常活動</span>
                          <span v-else-if="n.status == 'event_lower'">活動已下架或尚未開始</span>
                          <span v-else-if="n.status != 'shelves'">{{ t("carts.ProductOff") }}</span>
                          <span v-else-if="n.quantity > n.max_order_quantity">已超過最大訂購上限 {{ n.max_order_quantity }}</span>
                          <span v-else-if="n.inventory < 10">
                            {{ $t("carts.ProductRemaining", n.inventory, [n.inventory]) }}
                          </span>
                        </div>
                      </div>

                      <!-- 金額 -->
                      <div class="col-6 col-md-2 product-item__total-price">
                        <!-- 網紅價 -->
                        <div v-if="n.discounted_price && n.discounted_price != n.sale_price">
                          <div style="color: #9e9e9e;">
                            <s>{{ n.symbol }} {{ currencyFormat(n.sale_price * n.quantity) }}</s>
                          </div>
                          <div>
                            {{ n.symbol }} {{ currencyFormat(n.discounted_price * n.quantity) }}
                          </div>
                        </div>
                        <span v-else>
                          {{ n.symbol }} {{ currencyFormat(n.sale_price * n.quantity) }}
                        </span>
                      </div>

                      <!-- 動作(table) -->
                      <div class="col-sm-1 d-none d-md-block">
                        <div class="product-item__tool">
                          <i class="bi bi-x-circle-fill product-item-tool__icon" style="cursor: pointer;" @click="deleteCart(n.cart_id)"></i>
                        </div>
                      </div>

                    </div>
                    <!-- 配送方式 -->
                    <div class="pt-2 d-flex flex-wrap product-item__delivery">
                      <div class="py-1 pr-2">{{ t("carts.shippingMethod") }}:</div>
                      <div>
                        <el-radio-group v-model="n.logistic_type" @change="(val) => handleLogisticType(val, n.cart_id, gk)">
                          <el-radio v-for="logistic_type in n.available_logistic_types" :label="logistic_type">
                            {{ logisticMap[logistic_type].name }}
                          </el-radio>
                        </el-radio-group>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TODO: 贈品可選數量控制 -->
          <div v-if="g.choose_gifts.length > 0" class="col-12">
            <ChooseGift
              :max-quantity="g.choose_gift_qty"
              :key-id="g.cart_group_key"
              :data="g.choose_gifts"
            />
          </div>

          <div class="col-12 col-md-6 offset-md-5 product-sector__group-calc">
            <div class="total-price-calc-sector">
              <div class="total-price-calc__total">
                <div>{{ t("orders.subtotal") }}</div>
                <div><span>{{ state.currency_symbol }} {{ currencyFormat(g.assort_org_total) }}</span></div>
              </div>
              <div v-if="g.assort_discount > 0" class="total-price-calc__total">
                  <div>{{ t("orders.assortDiscount") }}</div>
                  <div><span>- {{ state.currency_symbol }} {{ currencyFormat(g.assort_discount) }}</span></div>
              </div>
              <div v-if="g.prom_discount > 0" class="total-price-calc__total">
                  <div>{{ t("orders.promDiscount") }}</div>
                  <div><span>- {{ state.currency_symbol }} {{ currencyFormat(g.prom_discount) }}</span></div>
              </div>
              <div class="total-price-calc__total">
                <div>{{ t("orders.total") }}</div>
                <div><span>{{ state.currency_symbol }} {{ groupTotal(g) }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch } from 'vue'
import { useI18n } from "vue-i18n"
import { useVModel, useVModels } from '@vueuse/core'
import { currencyFormat, dataTranslations as dt } from "../../utils"
import ChooseGift from "../ChooseGift.vue"

const props = defineProps({
  state: Object,
  data: Object,
  form: Object,
  cartEventGroupsFormatted: Object,
})
const emit = defineEmits(['update:state', 'update:data', 'delete', 'change'])

const { t, locale } = useI18n()
const { state, data, form } = useVModels(props, emit)

const logisticMap = computed(() => _.keyBy(data.value.logistics, "type"));
const cartCount = computed(() => form.value.carts.reduce((count, cart) => (count += cart.checked?cart.quantity:0), 0));
const prePayOpts = computed(() => {
  let prePay = [];

  const common = data.value.payments
    .filter((el) => (el.type.substr(-4) !== ".COD" || el.type == "ECPAY"))
    .map((el) => el.type);

  form.value.carts.forEach((el) => {
    const tmp = common.filter((val) => el.available_payment_types.indexOf(val) != -1)
    if (!prePay.length) {
      prePay = tmp
    } else {
      prePay = prePay.filter((val) => tmp.indexOf(val) != -1)
    }
  });
  return prePay;
})

const removeDiscountCode = () => {
  // console.log("", state.value.discountCode)
  if (state.value.discountCode) {
    alert("執行的動作將取消已選取的折價券，請再次重選適用的折價券")
    state.value.discountCode = null
    form.value.prom_code_id = null
  }
  return false
}

const handleCheckAllChange = (val) => {
  form.value.carts.forEach(cart => {
    let checked = val && cart.is_valid ? true : false
    cart.checked = checked
  })
  state.value.isIndeterminate = false
  removeDiscountCode()
}

const deleteCart = (cartId) => {
  removeDiscountCode()
  emit("delete", cartId)
}
const changeQty = (cartId, qty) => {
  // console.log("change: ", cartId, qty);
  removeDiscountCode()
  emit("change", {cartId, qty})
}
const handleLogisticType = (val, cart_id, guuid) => {
  // console.log("handleLogisticType: ", val, guuid)
  const item = form.value.carts.find((el) => el.cart_id === cart_id)
  // console.log("item: ", item)
  item.logistic_type = val

  if (guuid != 'null') {
    // console.log("guuid: ", guuid)
    // 同個活動內商品需選用同種物流
    form.value.carts.forEach((cart) => {
      if (cart.event_group && guuid == cart.event_group.event_group_uuid) {
        cart.logistic_type = val
        // console.log("cart: ", cart)
      }
    })
  }
  handlePayment()
}
const groupTotal = (g) => {
  return currencyFormat(g.assort_org_total - g.assort_discount - g.prom_discount)
}
const handleChange = (val, cart_id) => {
  const item = form.value.carts.find((el) => el.cart_id === cart_id)
  item.checked = val
  removeDiscountCode()
}
const handlePayment = () => {
  // console.log("prePayOpts: ", prePayOpts.value)
  form.value.carts.forEach((cart) => {
    // console.log("cart: ", cart.logistic_type)

    let is_valid = false
    let checked = false
    if (cart.status == 'shelves' && cart.quantity <= cart.max_order_quantity) {
      is_valid = true
      checked = true
    }

    // 使用貨到付款：檢查可勾選的商品
    // console.log("payment_type: ", props.form.payment_type, !prePayOpts.value.includes(props.form.payment_type))
    if (!prePayOpts.value.includes(props.form.payment_type)) {
      if (props.form.payment_type != cart.logistic_type+".COD") {
        // console.log("logistic_type: ", cart.logistic_type+".COD")
        checked = false
        is_valid = false
      }
    }
    // 檢查外島配送
    if (state.value.isOuterIsland[cart.logistic_type] && cart.is_excluded_outer_island) {
      // console.debug("檢查外島配送: ", cart.is_excluded_outer_island, state.value.isOuterIsland[cart.logistic_type])
      checked = false
      is_valid = false
    }

    cart.is_valid = is_valid;
    cart.checked = checked;

  })
  removeDiscountCode()
}
watch(
  () => props.form.payment_type,
  (val) => {
    handlePayment()
  },
  { deep: false, immediate: true }
)
</script>
